import React, { useMemo, useRef } from 'react';
import { Heart, IconFormIcons } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';
import { TaskActionLifeArea } from 'shared/types/task-action';

import { ActionTaskContextMenu } from './action-task-context-menu';
import * as Styled from './event.style';

export type LifeAreaEventProps = {
  task: Task;
  lifeAreas: LifeArea[];
  onClick?: (id: string) => void;
  marginTop: number;
  lineHeight: number;
  numberOfLines: number;
  onInteractionComplete?: () => void;
};

export const TaskLifeAreaEvent: React.FC<LifeAreaEventProps> = ({
  task,
  lifeAreas,
  onClick,
  marginTop,
  lineHeight,
  numberOfLines,
  onInteractionComplete,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const containerRef = useRef<HTMLDivElement>(null);
  const _onClick = () => onClick?.(task.id);

  const lifeArea = useMemo(
    () =>
      lifeAreas.find(
        ({ id }) => id === (task.action as TaskActionLifeArea)?.meta?.id,
      ),
    [lifeAreas, task.action],
  );

  if (!lifeArea) {
    return null;
  }

  return (
    <>
      <Styled.Container
        onClick={_onClick}
        onContextMenu={openMenu}
        $color={lifeArea.color ?? undefined}
        $completed={!!task.completedAt}
        ref={containerRef}
      >
        <Styled.Content>
          <Styled.IconContainer $color={lifeArea.color ?? undefined}>
            <Icon icon={IconFormIcons[lifeArea.iconName] ?? Heart} />
          </Styled.IconContainer>
          <Styled.Title
            $marginTop={marginTop}
            $lineHeight={lineHeight}
            $completed={!!task.completedAt}
            $numberOfLines={numberOfLines}
          >
            {lifeArea.name}
          </Styled.Title>
        </Styled.Content>
      </Styled.Container>

      {!!menuPosition && (
        <ActionTaskContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          onAction={onInteractionComplete}
        />
      )}
      <Tooltip
        referenceElement={containerRef}
        label={lifeArea.name}
        delay={400}
      />
    </>
  );
};
